import { Box } from "@mui/material";
import ArrowsDiagram from "../../assets/svg/ArrowsDiagram";
import { styles } from "./stylesPurchased";
import CustomSelect from "../../components/UI/CustomSelect/CustomSelect";
import { useEffect, useMemo, useRef, useState } from "react";
import { PurchaseRow } from "./component/PurchaseRow";
import { useGetWalletHistoryQuery } from "@/store/api/walletApi";
import { useAppSelector } from "@/hooks/useAppSelector";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import { PREMIUM_TIME_OPTIONS, TIME_OPTIONS, TimeRangeValue } from "@/types/timeRanges";

const industries = ["All", "withdrawal", "deposit", "like", "zap"];

type PurchaseItem = {
  key: string;
  title: string;
  date: string;
  amount: number;
  sats: string;
  positive: boolean;
  status: "PENDING" | "COMPLETED" | "FAILED";
  relatedEntityId?: string;
  fee?: number;
  createdAt: string;
};
const LIMIT = 50;
const Purchased = () => {
  const { user: authUser } = useAppSelector((state) => state.auth);
  const currentUid = authUser?.uid;
  const cleanTitle = (note?: string | null, type?: string, network?: string) => {
    if (note) {
      return note.includes(":") ? note.split(":")[0].trim() : note.trim();
    }
    const normalizedType = String(type ?? "").toUpperCase();
    const formattedNetwork = network
      ? network.charAt(0).toUpperCase() + network.slice(1).toLowerCase()
      : "";
    if (normalizedType === "WITHDRAWAL")
      return formattedNetwork ? `Withdraw ${formattedNetwork}` : "Withdrawal";
    if (normalizedType === "DEPOSIT")
      return formattedNetwork ? `Deposit ${formattedNetwork}` : "Deposit";
    return type ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase() : "Transaction";
  };
  const { subscriptionStatus } = useAppSelector((state) => state.auth);
  const hasPremium = subscriptionStatus === "ACTIVE";

  const [page, setPage] = useState(1);
  const [allPurchases, setAllPurchases] = useState<PurchaseItem[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const [timeRange, setTimeRange] = useState<TimeRangeValue>("24h");
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>(["All"]);

  const [forceUpdate, setForceUpdate] = useState(0);
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

  const selectedType = useMemo<("deposit" | "withdrawal" | "like" | "zap")[] | undefined>(() => {
    const allowed = ["deposit", "withdrawal", "like", "zap"] as const;
    const filtered = selectedIndustries.filter((v): v is (typeof allowed)[number] =>
      (allowed as readonly string[]).includes(v)
    );
    return filtered.length > 0 ? filtered : undefined;
  }, [selectedIndustries]);

  const queryArgs = useMemo(
    () => ({
      page,
      limit: LIMIT,
      time_range: timeRange || undefined,
      type: selectedType,
      order: sortOrder,
      _force: forceUpdate,
    }),
    [page, timeRange, forceUpdate, selectedType, sortOrder]
  );

  const { data, isFetching } = useGetWalletHistoryQuery(queryArgs, {
    skip: !currentUid,
    refetchOnMountOrArgChange: true,
  });

  const renderSelectedFilter = (selected: string[]) => {
    if (selected.length === 0) return "All";
    if (selected.length === 1) return selected[0];
    if (selected.length === industries.length) return "All";
    return `${selected.length} Selected`;
  };

  const handleSortClick = () => {
    setAllPurchases([]);
    setPage(1);
    setHasMore(true);
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };
  const mapTransactionToPurchase = (tx: any): PurchaseItem => {
    const type = String(tx.transactionType ?? "").toLowerCase();
    const isIncome =
      type === "withdrawal"
        ? false
        : type === "deposit"
          ? true
          : String(tx.sender?.uid) !== String(currentUid);
    const amount = tx.netAmount ?? 0;
    const fee = tx.fee ?? tx.platformFee ?? 0;
    return {
      key: tx.uid ?? `${tx.createdAt}-${tx.totalAmount}-${tx.sender?.uid ?? ""}`,
      title: cleanTitle(tx.note, tx.transactionType, tx.payoutNetwork),
      date: new Date(tx.createdAt).toLocaleString(),
      amount: Number(amount),
      sats: `${Math.round(amount)} SATS`,
      positive: isIncome,
      status: tx.status,
      relatedEntityId: tx.relatedEntityId,
      fee,
      createdAt: tx.createdAt,
    };
  };

  useEffect(() => {
    if (!data?.data) return;

    const mapped = data.data.map((tx) => mapTransactionToPurchase(tx));

    if (page === 1) {
      setAllPurchases(mapped);
    } else {
      setAllPurchases((prev) => {
        const seen = new Set(prev.map((p) => p.key));
        return [...prev, ...mapped.filter((p) => !seen.has(p.key))];
      });
    }

    setHasMore(data.data.length === LIMIT);
  }, [data, page]);

  const hasMoreRef = useRef(hasMore);
  const isFetchingRef = useRef(isFetching);
  const allPurchasesLengthRef = useRef(allPurchases.length);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    isFetchingRef.current = isFetching;
  }, [isFetching]);
  useEffect(() => {
    allPurchasesLengthRef.current = allPurchases.length;
  }, [allPurchases.length]);

  useEffect(() => {
    if (!loadMoreRef.current) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMoreRef.current &&
          !isFetchingRef.current &&
          allPurchasesLengthRef.current > 0
        ) {
          setPage((prev) => prev + 1);
        }
      },
      {
        root: scrollRef.current,
        threshold: 0.1,
      }
    );

    observer.current.observe(loadMoreRef.current);

    return () => observer.current?.disconnect();
  }, []);

  useEffect(() => {
    setPage(1);
    // setAllPurchases([]);
    setHasMore(true);
  }, [selectedIndustries, timeRange, sortOrder]);

  const sortedPurchases = useMemo(() => {
    const toTime = (createdAt?: string) => new Date(createdAt ?? 0).getTime() || 0;
    return [...allPurchases].sort((a, b) =>
      sortOrder === "asc"
        ? toTime(a.createdAt) - toTime(b.createdAt)
        : toTime(b.createdAt) - toTime(a.createdAt)
    );
  }, [allPurchases, sortOrder]);

  const onChange = (labels: string[]) => {
    const label = labels[0] ?? "";
    const value =
      [...TIME_OPTIONS, ...PREMIUM_TIME_OPTIONS].find((o) => o.label === label)?.value ?? "";

    setAllPurchases([]);
    setPage(1);
    setHasMore(true);

    if (value === timeRange) {
      setForceUpdate((prev) => prev + 1);
    } else {
      setTimeRange(value);
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Box sx={styles.selectBox}>
          <Box sx={styles.sortBox}>
            <CustomSelect
              options={industries}
              selected={selectedIndustries}
              onChange={(vals) => {
                if (vals.length === 0) {
                  setSelectedIndustries(["All"]);
                } else if (vals.includes("All") && !selectedIndustries.includes("All")) {
                  setSelectedIndustries(["All"]);
                } else if (vals.includes("All") && selectedIndustries.includes("All")) {
                  setSelectedIndustries(vals.filter((v) => v !== "All"));
                } else {
                  setSelectedIndustries(vals);
                }
              }}
              placeholder="All industries"
              checkbox
              multiple={true}
              width="454px"
              renderValue={renderSelectedFilter}
            />
            <CustomSelect
              options={[...TIME_OPTIONS, ...PREMIUM_TIME_OPTIONS].map((opt) => opt.label)}
              selected={
                timeRange
                  ? [
                      [...TIME_OPTIONS, ...PREMIUM_TIME_OPTIONS].find((o) => o.value === timeRange)
                        ?.label ?? "",
                    ]
                  : []
              }
              onChange={onChange}
              placeholder="Select time range"
              width="454px"
              disabledOptions={!hasPremium ? PREMIUM_TIME_OPTIONS.map((o) => o.label) : []}
            />

            <Box
              sx={{
                ...styles.ArrowsDiagram,
                transform: sortOrder === "asc" ? "scaleY(-1)" : "none",
                transition: "transform 0.2s ease",
              }}
              onClick={handleSortClick}
              title={sortOrder === "desc" ? "Newest first" : "Oldest first"}
            >
              <ArrowsDiagram />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={styles.purchasedList} className="hide-scrollbar purchased-scroll">
        {isFetching && allPurchases.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: "auto", height: "100%" }}>
            <CustomLoader size={32} />
          </Box>
        )}

        {sortedPurchases.map((item) => (
          <PurchaseRow
            key={item.key}
            title={item.title}
            date={item.date}
            amount={item.amount}
            positive={item.positive}
            relatedEntityId={item.relatedEntityId}
            fee={item.fee}
          />
        ))}

        {/* sentinel */}

        <div ref={loadMoreRef} style={{ height: 1 }} />

        {isFetching && page > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CustomLoader size={32} />
          </Box>
        )}

        {!hasMore && allPurchases.length > 0 && (
          <Box sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
            No more transactions
          </Box>
        )}
      </Box>
    </Box>
  );
};
export default Purchased;
